/**
 * Definición de indicadores del Sistema de Gestión de Calidad
 * @author Jorge Villalón <jorge.villalon@uai.cl>
 */

import { isNumber } from "lodash";
import { debug } from "loglevel";
import diccionarioEncuestas from "../data/diccionarioencuestas.json";

const validarValor = (_value) => {
  if (_value === undefined || _value === null) {
    return 0;
  }
  return _value;
};
const displayNumber = (_num, decimales = 2) => {
  if (_num === undefined || _num === null || !isNumber(_num)) {
    return _num;
  }
  return _num.toFixed(decimales);
};

const estadisticas = {
  ESP: {
    satisfaccion_general: {
      key_preguntas: ["Nivel de satisfacción general con el programa"],
      preguntas:
        diccionarioEncuestas["Nivel de satisfacción general con el programa"][
          "preguntas"
        ],
    },
    calidad_academica: {
      key_preguntas: ["Nivel de satisfacción con la calidad académica"],
      preguntas:
        diccionarioEncuestas["Nivel de satisfacción con la calidad académica"][
          "preguntas"
        ],
    },
    gestion_coordinadora: {
      key_preguntas: [
        "Nivel de satisfacción con la gestión de la coordinación del programa",
      ],
      preguntas:
        diccionarioEncuestas[
          "Nivel de satisfacción con la gestión de la coordinación del programa"
        ]["preguntas"],
    },
    relevancia_contenidos: {
      key_preguntas: [
        "Nivel de satisfacción con la relevancia de los contenidos",
      ],
      preguntas:
        diccionarioEncuestas[
          "Nivel de satisfacción con la relevancia de los contenidos"
        ]["preguntas"],
    },
    recomendacion: {
      key_preguntas: ["Nivel de recomendación del programa, NPS"],
      preguntas:
        diccionarioEncuestas["Nivel de recomendación del programa, NPS"][
          "preguntas"
        ],
    },
  },
};

const obtieneEstadistica = (data, encuesta, indicador, estadigrafo) => {
  if (!data || !data.preguntas) {
    throw new Error(
      "Datos inválidos para obtener estadística: data o data.preguntas"
    );
  }
  if (!estadisticas[encuesta]) {
    throw new Error("No existe la encuesta " + encuesta);
  }
  if (!estadisticas[encuesta][indicador]) {
    throw new Error(
      "No existe el indicador " + indicador + " para la encuesta " + encuesta
    );
  }
  let stat = undefined;
  estadisticas[encuesta][indicador].preguntas.forEach((pregunta) => {
    if (stat === undefined && data.preguntas[pregunta]) {
      if (data.preguntas[pregunta][estadigrafo]) {
        stat = data.preguntas[pregunta][estadigrafo];
        return;
      } else {
        throw new Error(
          "No existe estadígrafo " +
            estadigrafo +
            " para la pregunta " +
            pregunta +
            " indicador " +
            indicador +
            " encuesta " +
            encuesta
        );
      }
    }
  });
  if (!stat) {
    throw new Error(
      "No existe estadígrafo " +
        estadigrafo +
        " para indicador " +
        indicador +
        " encuesta " +
        encuesta
    );
  }
  return stat;
};

// Faltan indicadores para EIP, EAM y EDOC
const indicadores = {
  // kpi_ESP_recomendacion_nps
  kpi_nps_recomendacion: {
    encuesta: "ESP",
    title: "NPS de Recomendación",
    value: (cohorte) => {
      let _data = cohorte.parsed_encuesta_salida_programa;
      return obtieneEstadistica(_data, "ESP", "recomendacion", "isn.nps");
    },
    display: (_value) => {
      let _val = _value * 100;
      _val = _val.toFixed(0);
      return _val + "%";
    },
    question: "¿Recomendarías el programa que cursaste a un amigo o conocido?",
    alert: (_value) => {
      if (_value < 0.6) {
        return true;
      }
      return false;
    },
    sample: (cohorte) => {
      let _data = cohorte.parsed_encuesta_salida_programa;
      if (
        _data === undefined ||
        _data === null ||
        !_data.hasOwnProperty("respuestas")
      ) {
        return undefined;
      } else {
        return _data["respuestas"];
      }
    },
  },
  //Cambio tentativo a proxima version de KPI
  // kpi_ESP_satisfaccion_programa_avg
  kpi_avg_satisfaccion_programa_salida: {
    encuesta: "ESP",
    title: "Nota promedio del Nivel de Satisfacción con el Programa",
    value: (cohorte) => {
      let _data = cohorte.parsed_encuesta_salida_programa;
      return obtieneEstadistica(_data, "ESP", "satisfaccion_general", "avg");
    },
    display: (_value) => {
      let _val = displayNumber(_value, 2);
      return _val;
    },
    question: "¿Cuál es tu nivel de satisfacción general?",
    alert: (_value) => {
      if (_value < 5.5) {
        return true;
      }
      return false;
    },
    sample: (cohorte) => {
      let _data = cohorte.parsed_encuesta_salida_programa;
      if (
        _data === undefined ||
        _data === null ||
        !_data.hasOwnProperty("respuestas")
      ) {
        return undefined;
      } else {
        return _data["respuestas"];
      }
    },
  },
  kpi_ratio_satisfaccion_academica: {
    encuesta: "ESP",
    title:
      "Ratio nota promedio calidad académica/nota promedio satisfacción con el programa",
    value: (_data) => {
      if (!_data.parsed_encuesta_salida_programa) {
        return undefined;
      }
      let avg_satisfaccion_programa = obtieneEstadistica(
        _data.parsed_encuesta_salida_programa,
        "ESP",
        "satisfaccion_general",
        "avg"
      );
      let avg_calidad_academica = obtieneEstadistica(
        _data.parsed_encuesta_salida_programa,
        "ESP",
        "calidad_academica",
        "avg"
      );
      if (!isNaN(avg_calidad_academica) && avg_satisfaccion_programa > 0) {
        return avg_calidad_academica / avg_satisfaccion_programa;
      } else {
        return 0;
      }
    },
    display: (_value) => {
      return displayNumber(_value, 2);
    },
    question:
      "¿Cuál es tu nivel de satisfacción con la calidad académica del programa?",
    alert: (_value) => {
      if (_value < 0.92) {
        return true;
      }
      return false;
    },
    sample: (cohorte) => {
      let _data = cohorte.parsed_encuesta_salida_programa;
      if (
        _data === undefined ||
        _data === null ||
        !_data.hasOwnProperty("respuestas")
      ) {
        return undefined;
      } else {
        return _data["respuestas"];
      }
    },
  },
  kpi_ratio_satisfaccion_coordinador: {
    encuesta: "ESP",
    title:
      "Ratio nota promedio gestión coordinadora/nota promedio satisfacción con el programa",
    value: (_data) => {
      if (!_data.parsed_encuesta_salida_programa) {
        return undefined;
      }
      let avg_satisfaccion_programa = obtieneEstadistica(
        _data.parsed_encuesta_salida_programa,
        "ESP",
        "satisfaccion_general",
        "avg"
      );
      let avg_satisfaccion_coordinadora = obtieneEstadistica(
        _data.parsed_encuesta_salida_programa,
        "ESP",
        "gestion_coordinadora",
        "avg"
      );
      if (
        !isNaN(avg_satisfaccion_coordinadora) &&
        avg_satisfaccion_programa > 0
      ) {
        return avg_satisfaccion_coordinadora / avg_satisfaccion_programa;
      } else {
        return undefined;
      }
    },
    display: (_value) => {
      let _val = displayNumber(_value, 2);
      return _val;
    },
    question:
      "¿Cuál es tu nivel de satisfacción con la gestión de la coordinación académica?",
    alert: (_value) => {
      if (_value < 0.92) {
        return true;
      }
      return false;
    },
    sample: (cohorte) => {
      let _data = cohorte.parsed_encuesta_salida_programa;
      if (_data === undefined || _data === null) {
        return undefined;
      } else {
        return _data["respuestas"];
      }
    },
  },
  kpi_ratio_satisfaccion_contenidos: {
    encuesta: "ESP",
    title:
      "Ratio nota promedio relevancia de contenidos/nota promedio satisfacción con el programa",
    value: (_data) => {
      if (!_data.parsed_encuesta_salida_programa) {
        return undefined;
      }
      let avg_satisfaccion_programa = obtieneEstadistica(
        _data.parsed_encuesta_salida_programa,
        "ESP",
        "satisfaccion_general",
        "avg"
      );
      let avg_satisfaccion_contenidos = obtieneEstadistica(
        _data.parsed_encuesta_salida_programa,
        "ESP",
        "relevancia_contenidos",
        "avg"
      );
      if (
        !isNaN(avg_satisfaccion_contenidos) &&
        avg_satisfaccion_programa > 0
      ) {
        return avg_satisfaccion_contenidos / avg_satisfaccion_programa;
      } else {
        return undefined;
      }
    },
    display: (_value) => {
      let _val = displayNumber(_value, 2);
      return _val;
    },
    question:
      "¿Cuál es tu nivel de satisfacción con la relevancia de los contenidos del programa?",
    alert: (_value) => {
      if (_value < 0.92) {
        return true;
      }
      return false;
    },
    sample: (cohorte) => {
      let _data = cohorte.parsed_encuesta_salida_programa;
      if (
        _data === undefined ||
        _data === null ||
        !_data.hasOwnProperty("respuestas")
      ) {
        return undefined;
      } else {
        return _data["respuestas"];
      }
    },
  },
  kpi_avg_recomendacion: {
    encuesta: "ESP",
    title: "Nota promedio de si recomendaría el programa",
    value: (cohorte) => {
      let _data = cohorte.parsed_encuesta_salida_programa;
      return obtieneEstadistica(_data, "ESP", "recomendacion", "avg");
    },
    display: (_value) => {
      let _val = displayNumber(_value, 2);
      return _val;
    },
    question: "¿Recomendarías el programa que cursaste a un amigo o conocido?",
    alert: (_value) => {
      if (_value < 8) {
        return true;
      }
      return false;
    },
    sample: (cohorte) => {
      let _data = cohorte.parsed_encuesta_salida_programa;
      if (
        _data === undefined ||
        _data === null ||
        !_data.hasOwnProperty("respuestas")
      ) {
        return undefined;
      } else {
        return _data["respuestas"];
      }
    },
  },
};
export default indicadores;
